// askSmart-ui/src/pages/TOSP/components/NaturalBreakpointDetail.tsx
import React from "react";
import { formatCurrency } from "../format";
import { NaturalBreakpointYear } from "../types";
import "./NaturalBreakpointDetail.css";

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "Jan 1 – Jun 30": both ends of a period, without the year, which the panel's
// heading already carries.
const periodLabel = (startISO: string, endISO: string): string => {
  const short = (iso: string) => {
    const [, month, day] = iso.split("-").map(Number);
    return month && day ? `${MONTH_ABBREVIATIONS[month - 1]} ${day}` : iso;
  };
  return `${short(startISO)} – ${short(endISO)}`;
};

const formatRate = (rate: number | null): string =>
  rate === null ? "—" : `${rate.toLocaleString(undefined, { maximumFractionDigits: 4 })}%`;

interface NaturalBreakpointDetailProps {
  breakdown: NaturalBreakpointYear;
  /** Overrides the default "{year} Natural Breakpoint" heading. */
  heading?: string;
  /** Hides the panel's own total — for a caller printing a combined one. */
  hideTotal?: boolean;
}

// How a year's natural breakpoint was arrived at: one block per stretch of the
// year the rent held steady, then the year's figure.
//
// Shared by the admin lease form and the tenant portal deliberately. A tenant
// querying their breakpoint and the property manager checking it should be
// looking at the same workings, not at two implementations that might drift.
export default function NaturalBreakpointDetail({
  breakdown,
  heading,
  hideTotal = false,
}: NaturalBreakpointDetailProps) {
  return (
    <div className="tosp-breakpoint-detail">
      <div className="tosp-breakpoint-heading">
        {heading || `${breakdown.year} Natural Breakpoint`}
      </div>

      {breakdown.periods.length === 0 ? (
        <p className="tosp-breakpoint-note">
          No Minimum Rent is payable during {breakdown.year}, therefore no Natural
          Breakpoint applies.
        </p>
      ) : (
        <div className="tosp-breakpoint-periods">
          {breakdown.periods.map((period) => (
            <div key={period.start} className="tosp-breakpoint-period">
              {/* The period the rent applies over, and nothing else. The
                  proration behind the year's figure is worked in days, but the
                  lease talks in periods, so that's what's shown. */}
              <div className="tosp-breakpoint-period-head">
                {periodLabel(period.start, period.end)}
              </div>
              <div className="tosp-breakpoint-line">
                <span>Annual Minimum Rent</span>
                <span>
                  {period.annualMinimumRent === null
                    ? "—"
                    : formatCurrency(period.annualMinimumRent)}
                </span>
              </div>
              <div className="tosp-breakpoint-line">
                <span>Breakpoint Rate</span>
                <span>{formatRate(breakdown.breakpointRate)}</span>
              </div>
              <div className="tosp-breakpoint-line tosp-breakpoint-line-strong">
                <span>Natural Breakpoint</span>
                <span>
                  {period.naturalBreakpoint === null
                    ? "—"
                    : formatCurrency(period.naturalBreakpoint)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Each period's breakpoint is an annual figure, so the year's is those
          figures over the days each one is actually in force. */}
      {!hideTotal && (
        <div className="tosp-breakpoint-total">
          <span>Current Calendar Year Natural Breakpoint</span>
          <strong>{breakdown.total === null ? "—" : formatCurrency(breakdown.total)}</strong>
        </div>
      )}

      {breakdown.periods.length > 1 && (
        <p className="tosp-breakpoint-note">
          Calculated from the Natural Breakpoint for each rent period in effect during the
          current calendar year.
        </p>
      )}
    </div>
  );
}
